import {FC} from 'react';
import {Box, Typography} from '@mui/material';
import {Form, Formik, FormikProps} from 'formik';
import {useIntl} from 'react-intl';
import AMPModal from 'src/components/common/AMPModal';
import {AMPInput} from 'src/components/common/AMPInput';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {getHelperText} from 'src/utils/forms';
import {CurrencyEnum} from 'src/enums';
import {
    CustomInvoiceItemFormType,
    customInvoiceItemSchema,
    initialCustomInvoiceItem,
} from 'src/components/forms/formData/customInvoiceItem';
import {CreateCustomInvoiceItemModalProps} from './CreateCustomInvoiceItemModal.types';
import {footerSx, formBodySx, priceInputSx} from './CreateCustomInvoiceItemModal.sx';

export const CreateCustomInvoiceItemModal: FC<CreateCustomInvoiceItemModalProps> = ({
                                                                                        open,
                                                                                        onClose,
                                                                                        onSubmit,
                                                                                        initialValues,
                                                                                        title,
                                                                                        submitText,
                                                                                    }) => {
    const intl = useIntl();

    return (
        <AMPModal
            open={open}
            handleClose={onClose}
            title={intl.formatMessage({id: title ?? 'addInvoiceItem'})}
            type="sm"
            fullWidth
            withoutPaddings
        >
            <Formik
                initialValues={initialValues ?? initialCustomInvoiceItem}
                validationSchema={customInvoiceItemSchema}
                onSubmit={onSubmit}
                enableReinitialize
            >
                {({
                      errors,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      isSubmitting,
                      touched,
                      values,
                  }: FormikProps<CustomInvoiceItemFormType>) => (
                    <Form
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        <Box sx={formBodySx}>
                            <AMPInput
                                type="text"
                                value={values.textFrench}
                                label="descriptionFr"
                                input="textFrench"
                                placeholder="descriptionFr"
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                hasError={touched.textFrench && Boolean(errors.textFrench)}
                                helperText={getHelperText('textFrench', touched, errors, intl)}
                            />
                            <AMPInput
                                type="text"
                                value={values.text}
                                label="descriptionEn"
                                input="text"
                                placeholder="descriptionEn"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                hasError={touched.text && Boolean(errors.text)}
                                helperText={getHelperText('text', touched, errors, intl)}
                            />
                            <AMPInput
                                type="text"
                                value={values.textDeutsch}
                                label="descriptionDe"
                                input="textDeutsch"
                                placeholder="descriptionDe"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                hasError={touched.textDeutsch && Boolean(errors.textDeutsch)}
                                helperText={getHelperText('textDeutsch', touched, errors, intl)}
                            />
                            <AMPInput
                                type="number"
                                value={values.price}
                                label="priceInclVat"
                                input="price"
                                placeholder="priceInclVat"
                                required
                                sx={priceInputSx}
                                endAdornment={
                                    <Typography variant="subtitle2" color="textPrimary">
                                        {CurrencyEnum.CHF}
                                    </Typography>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                hasError={touched.price && Boolean(errors.price)}
                                helperText={getHelperText('price', touched, errors, intl)}
                            />
                            <AMPInput
                                type="number"
                                value={values.quantity}
                                label="quantity"
                                input="quantity"
                                placeholder="quantity"
                                required
                                sx={priceInputSx}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                hasError={touched.quantity && Boolean(errors.quantity)}
                                helperText={getHelperText('quantity', touched, errors, intl)}
                            />
                        </Box>
                        <Box sx={footerSx}>
                            <CancelButton
                                text="cancel"
                                handler={onClose}
                            />
                            <SubmitButton
                                text={submitText ?? 'save'}
                                color="primary"
                                isSubmitting={isSubmitting}
                            />
                        </Box>
                    </Form>
                )}
            </Formik>
        </AMPModal>
    );
};
